/**
 * Search and Filter Utilities
 * 
 * Provides functions for filtering navigation cards and debouncing search input.
 */

/**
 * Filters navigation items based on search query
 * Searches in both title and description fields
 * 
 * @param {Array} items - Array of navigation items to filter
 * @param {string} query - Search query string
 * @returns {Array} Filtered array of navigation items
 */
export const filterNavigationBySearch = (items, query) => {
  if (!query || query.trim() === '') {
    return items;
  }

  const searchTerm = query.toLowerCase().trim();

  return items.filter((item) => {
    // Search in title
    const titleMatch = item.title.toLowerCase().includes(searchTerm);
    
    // Search in description if it exists
    const descriptionMatch = item.description 
      ? item.description.toLowerCase().includes(searchTerm)
      : false;

    // Search in sub-items titles if they exist
    const subItemMatch = item.subItems && item.subItems.length > 0
      ? item.subItems.some(subItem => 
          subItem.title.toLowerCase().includes(searchTerm)
        )
      : false;

    return titleMatch || descriptionMatch || subItemMatch;
  });
};

/**
 * Creates a debounced version of a function
 * Delays execution until after a specified wait time has elapsed
 * 
 * @param {function} func - Function to debounce
 * @param {number} wait - Delay in milliseconds (default: 300ms)
 * @returns {function} Debounced function
 */
export const debounce = (func, wait = 300) => {
  let timeout;

  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Highlights matching text in a string
 * Returns an array of text segments with match information
 * 
 * @param {string} text - Text to highlight
 * @param {string} query - Search query to highlight
 * @returns {Array} Array of objects with {text, isMatch} properties
 */
export const highlightText = (text, query) => {
  if (!query || !text) {
    return [{ text, isMatch: false }];
  }

  const searchTerm = query.toLowerCase().trim();
  const lowerText = text.toLowerCase();
  const segments = [];
  let lastIndex = 0;

  // Find all occurrences of the search term
  let index = lowerText.indexOf(searchTerm);
  
  while (index !== -1) {
    // Add non-matching text before the match
    if (index > lastIndex) {
      segments.push({
        text: text.substring(lastIndex, index),
        isMatch: false,
      });
    }

    // Add the matching text
    segments.push({
      text: text.substring(index, index + searchTerm.length),
      isMatch: true,
    });

    lastIndex = index + searchTerm.length;
    index = lowerText.indexOf(searchTerm, lastIndex);
  }

  // Add remaining non-matching text
  if (lastIndex < text.length) {
    segments.push({
      text: text.substring(lastIndex),
      isMatch: false,
    });
  }

  return segments;
};
